import React, {useMemo} from "react";
import isEmpty from "is-empty";
import {Collapse} from "@mui/material";
import classNames from "classnames";
import {useModalStore} from "@/Stores/Site/ModalStore";
import {formatDMY} from "@/shared/lib/date";
import {useReviews} from "@/features/product/model/useReviews";
import {declOfNum} from "@/Helpers/Function";
import Svg from "@/Components/Svg";
import StyledRating from "@/Pages/All/StyledRating";
import Lightbox from "@/Pages/All/Lightbox";
import "@/Scss/Site/Product/reviews.scss";

const Reviews = (props) => {
    const {product = {}, isMobile} = props;
    const {review = [], ratingAVG} = product;

    const {
        type,
        visibleReviews,
        reviewsWithImages,
        showMore,
        toggleExpand,
        expanded,
        changeType,
        hasMore
    } = useReviews(review);

    const ratingStats = useMemo(() => {
        return review.reduce((acc, el) => {
            acc[el.rating] = (acc[el.rating] || 0) + 1;
            return acc;
        }, {});
    }, [review]);

    // Количество символов в превью отзыва
    let numberCharacters = 100;
    if (isMobile) {
        numberCharacters = 40;
    }

    return (
        <div id={`reviews`} className={`reviews`}>
            <p className={`reviews__title`}>Отзывы</p>
            {visibleReviews.length === 0 && (
                <p className={`reviews__noReviews`}>На это украшение еще нет отзывов</p>
            )}
            {reviewsWithImages.length !== 0 && (
                <div className={`reviews__buttons`}>
                    <div
                        className={`button button--changeReviews${type === 'all' ? ' active' : ''}`}
                        onClick={() => changeType('all')}
                    >
                        Все отзывы ({review.length})
                    </div>
                    <div
                        className={`button button--changeReviews${type === 'withImages' ? ' active' : ''}`}
                        onClick={() => changeType('withImages')}
                    >
                        Отзывы с фото ({reviewsWithImages.length})
                    </div>
                </div>
            )}
            {visibleReviews.length !== 0 ?
                <div className={`reviews__body`}>
                    <div className={`reviews__left`}>
                        {visibleReviews.map(el => {
                            const textLength = el.text?.length ?? 0;

                            return (
                                <div key={el.id} className={`reviews__item`}>
                                    <div className={`reviews__header`}>
                                        <p className={`reviews__name`}>{el.author}</p>
                                        <p className={`reviews__date`}>{formatDMY(el.created_at)}</p>
                                        <div className={`reviews__stars`}>
                                            <StyledRating ratingAVG={Number(el.rating)} />
                                        </div>
                                    </div>
                                    {el.img && (
                                        <Lightbox patch={el.img.split('|')} className={`productReviewsPhoto`} />
                                    )}
                                    {textLength > 0 && (
                                        <>
                                            <div className={`reviews__content`}>
                                                <Collapse in={expanded[el.id]} collapsedSize={20}>
                                                    <p className={`reviewText`}>{el.text}</p>
                                                    {!isEmpty(el.answer) && (
                                                        <div className={`answerToReviewContainer`}>
                                                            <p className={`answerToReviewText`}>{el.answer}</p>
                                                        </div>
                                                    )}
                                                </Collapse>
                                            </div>
                                            {(textLength > numberCharacters || !isEmpty(el.answer)) && (
                                                <div className={`reviews__bottom`}>
                                                    <div className={`more`} onClick={() => toggleExpand(el.id)}>
                                                        {expanded[el.id] ? 'Скрыть' : 'Подробнее'}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                        {hasMore && (
                            <div className={`reviews__moreReviewsContainer`} onClick={showMore}>
                                <p className={`moreReviewsText`}>Смотреть еще</p>
                                <Svg id={`arrows-right`} className={`svg__moreReviewsArrows`} />
                            </div>
                        )}
                    </div>
                    <div className={`reviews__right`}>
                        <div className={`reviews__detailedDescription`}>
                            <div className={`info-wrap`}>
                                <div className={`info-wrap__item info-wrap__item_1`}>
                                    <div className={`averageMarkContainer`}>
                                        <p className={`averageMarkContainer__text`}>Оценка</p>
                                        <p className={`averageMarkContainer__mark`}>{ratingAVG}</p>
                                    </div>
                                    <div className={`all-reviews`}>
                                        <p className={`number-reviews`}>
                                            <span>{review.length} </span>
                                            {declOfNum(review.length, ['отзыв', 'отзыва', 'отзывов'])}
                                        </p>
                                    </div>
                                </div>
                                <div className={`info-wrap__item info-wrap__item_2`}>
                                    {[5, 4, 3, 2, 1].map((rate) => (
                                        <div key={rate} className={`el-wrapper`}>
                                            <StyledRating ratingAVG={rate} />
                                            <div className={`el-wrapper__desc`}>
                                                <span>{ratingStats[rate]} </span>
                                                <span>
                                                    {declOfNum(ratingStats[rate], ['отзыв', 'отзыва', 'отзывов'])}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <Button className={`button--question`} {...props} />
                    </div>
                </div>
                :
                <Button className={`button--questionNotReviews`} {...props} />
            }
        </div>
    );
};

const Button = (props) => {
    const {product = {}, authCheck = false} = props;
    const {id, reviewAdd = false} = product;
    const openModalWriteReview = useModalStore(state => state.openModalWriteReview);
    const openModalAuthOrRegister = useModalStore(state => state.openModalAuthOrRegister);

    if (!reviewAdd) return null;

    if (!authCheck) {
        return <div className={classNames('button', props.className)} onClick={openModalAuthOrRegister}>
            Авторизуйтесь, чтобы оставить отзыв
        </div>
    }

    return <div className={classNames('button', props.className)} onClick={() => openModalWriteReview(id)}>
        Добавить отзыв
    </div>
}

export default Reviews;
